import { useState, useRef } from 'react';
import {
  X, Download, Calendar, Clock, MapPin, User, Phone, Mail,
  Shield, Wrench, Camera, CheckSquare, MessageSquare,
  ClipboardList, Building, MapPinCheck, MapPinOff, Timer, AlertCircle, FileText, Printer
} from 'lucide-react';
import { cn } from '../../../utils/cn';
import { useUserPreferences } from '../../../contexts/UserPreferencesContext';
import { downloadPdf, printContent } from '../../../utils/pdfUtils';

// Days of the week for schedule display
const DAYS = [
  { key: 'mon', label: 'M', fullLabel: 'Monday' },
  { key: 'tue', label: 'T', fullLabel: 'Tuesday' },
  { key: 'wed', label: 'W', fullLabel: 'Wednesday' },
  { key: 'thu', label: 'T', fullLabel: 'Thursday' },
  { key: 'fri', label: 'F', fullLabel: 'Friday' },
  { key: 'sat', label: 'S', fullLabel: 'Saturday' },
  { key: 'sun', label: 'S', fullLabel: 'Sunday' }
];

// Format time from 24h to 12h or 24h based on preference
const formatTime = (time, format = '12h') => {
  if (!time) return null;
  const [hours, minutes] = time.split(':');
  const h = parseInt(hours, 10);

  if (format === '24h') {
    return `${hours}:${minutes}`;
  }

  const suffix = h >= 12 ? 'PM' : 'AM';
  const displayHour = h % 12 || 12;
  return `${displayHour}:${minutes} ${suffix}`;
};

// Format date based on preference
const formatDate = (dateStr, format = 'DD/MM/YYYY') => {
  if (!dateStr) return 'Not set';
  const date = new Date(dateStr);

  const day = date.getDate();
  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const weekday = weekdays[date.getDay()];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = months[date.getMonth()];
  const year = date.getFullYear();

  switch (format) {
    case 'MM/DD/YYYY':
      return `${weekday}, ${month} ${day}, ${year}`;
    case 'YYYY-MM-DD':
      return `${weekday}, ${year} ${month} ${day}`;
    case 'DD/MM/YYYY':
    default:
      return `${weekday}, ${day} ${month} ${year}`;
  }
};

// Get status badge color
const getStatusColor = (status) => {
  const colors = {
    created: 'bg-gray-100 text-gray-700',
    assigned: 'bg-blue-100 text-blue-700',
    in_progress: 'bg-yellow-100 text-yellow-700',
    completed: 'bg-green-100 text-green-700',
    approved: 'bg-emerald-100 text-emerald-700',
    paid: 'bg-purple-100 text-purple-700',
    cancelled: 'bg-red-100 text-red-700',
    submitted: 'bg-orange-100 text-orange-700'
  };
  return colors[status] || 'bg-gray-100 text-gray-700';
};

// Get priority color
const getPriorityColor = (priority) => {
  const colors = {
    high: 'bg-red-100 text-red-700',
    medium: 'bg-yellow-100 text-yellow-700',
    low: 'bg-green-100 text-green-700'
  };
  return colors[priority?.toLowerCase()] || 'bg-gray-100 text-gray-700';
};

// Week circles component for recurring schedule display
const WeekCircles = ({ schedule }) => {
  const activeDays = schedule?.days || [];
  const pattern = schedule?.pattern || 'fixed';
  const fixedTime = schedule?.time;
  const customTimes = schedule?.customTimes || {};

  const getTimeForDay = (dayKey) => {
    if (pattern === 'custom') {
      return customTimes[dayKey] || null;
    }
    return fixedTime || null;
  };

  // If no days are selected, show "Custom Schedule" message
  if (!activeDays || activeDays.length === 0) {
    return (
      <p className="text-sm text-gray-600 italic">Custom schedule (varies by instance)</p>
    );
  }

  return (
    <div className="flex items-center gap-1">
      {DAYS.map((day) => {
        const isActive = activeDays.includes(day.key);
        const time = getTimeForDay(day.key);
        return (
          <div
            key={day.key}
            className={cn(
              'w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium',
              isActive ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-400'
            )}
            title={isActive ? `${day.fullLabel}${time ? ` at ${formatTime(time)}` : ''}` : day.fullLabel}
          >
            {day.label}
          </div>
        );
      })}
    </div>
  );
};

// Format full datetime for display
const formatFullDateTime = (dateTimeStr, dateFormat = 'DD/MM/YYYY', timeFormat = '12h') => {
  if (!dateTimeStr) return 'Not recorded';
  try {
    const dt = new Date(dateTimeStr);
    const day = dt.getDate();
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[dt.getMonth()];
    const year = dt.getFullYear();
    const time = dt.toLocaleTimeString('en-AU', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: timeFormat === '12h'
    });

    switch (dateFormat) {
      case 'MM/DD/YYYY':
        return `${month} ${day}, ${year} at ${time}`;
      case 'YYYY-MM-DD':
        return `${year} ${month} ${day} at ${time}`;
      case 'DD/MM/YYYY':
      default:
        return `${day} ${month} ${year} at ${time}`;
    }
  } catch {
    return dateTimeStr;
  }
};

// Format hours and minutes display
const formatHoursMinutes = (decimalHours) => {
  if (!decimalHours || decimalHours === 0) return '0hr 0mn';
  const hours = Math.floor(decimalHours);
  const minutes = Math.round((decimalHours - hours) * 60);
  return `${hours}hr ${minutes}mn`;
};

export default function JobReport({
  job,
  instance,
  company,
  onClose,
  timesheet,
  overtimeRequest, // Overtime request data if available
  photos,
  workNotes,
  checklistData,
  questionnaireResponses,
  partnerAgency // Partner agency data if available
}) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const reportRef = useRef(null);
  const { preferences } = useUserPreferences();

  if (!job) return null;

  const isRecurring = job?.jobType === 'recurring';
  const isInstanceView = !!instance;
  const instances = job?.job_instances || [];
  const schedule = job?.recurringSchedule;

  // Check if report data is available (for completed jobs)
  const hasReportData = !!(timesheet || instance?.clock_in_time || instance?.clock_out_time || workNotes || checklistData || questionnaireResponses || photos?.length > 0);

  // Generate report number
  const reportNumber = isInstanceView
    ? `${job?.jobDisplayId || 'JOB'}-#${instance?.instance_number || 1}`
    : job?.jobDisplayId || 'JOB';

  // Calculate totals for recurring jobs
  const totalInstances = instances.length;
  const estimatedHours = job?.estimatedHours || 0;
  const totalHours = estimatedHours * totalInstances;

  // Company initials for logo placeholder
  const companyInitials = company?.company_name
    ?.split(' ')
    ?.map(w => w[0])
    ?.join('')
    ?.substring(0, 2)
    ?.toUpperCase() || 'CO';

  // Format company address from individual fields
  const formatCompanyAddress = () => {
    const parts = [];
    if (company?.address_line1) parts.push(company.address_line1);
    if (company?.address_line2) parts.push(company.address_line2);

    const cityStatePostcode = [
      company?.city,
      company?.state,
      company?.postcode
    ].filter(Boolean).join(' ');

    if (cityStatePostcode) parts.push(cityStatePostcode);
    if (company?.country && company?.country !== 'Australia') parts.push(company.country);

    return parts.length > 0 ? parts.join(', ') : null;
  };

  const companyAddress = formatCompanyAddress();
  const companyPhone = company?.phone;
  const companyEmail = company?.contact_email;

  // Separate photos by type
  const beforePhotos = photos?.filter(p => p.photo_type === 'before') || [];
  const afterPhotos = photos?.filter(p => p.photo_type === 'after') || [];
  const otherPhotos = photos?.filter(p => p.photo_type !== 'before' && p.photo_type !== 'after') || [];

  // Location verification check
  const clockInDistance = instance?.clock_in_distance || timesheet?.clock_in_distance;
  const clockInRadius = job?.clockInRadius;
  const isWithinRadius = clockInRadius && clockInDistance !== undefined
    ? clockInDistance <= clockInRadius
    : null;

  // Time compliance check
  const actualHours = parseFloat(timesheet?.total_hours || 0);
  const getTimeCompliance = () => {
    if (!estimatedHours || !actualHours) return null;
    const diff = actualHours - estimatedHours;
    if (diff <= 0) return { status: 'on-time', label: 'On Time', color: 'bg-green-100 text-green-700' };
    if (diff <= 0.5) return { status: 'slight-overtime', label: 'Slight Overtime', color: 'bg-yellow-100 text-yellow-700' };
    return { status: 'overtime', label: 'Overtime', color: 'bg-red-100 text-red-700' };
  };
  const timeCompliance = getTimeCompliance();

  // Handle PDF download using server-side Puppeteer rendering
  const handleDownloadPDF = async () => {
    if (!reportRef.current) return;

    setIsDownloading(true);
    await downloadPdf({
      contentRef: reportRef,
      filename: `JobReport-${reportNumber}`,
      onSuccess: () => {
        // PDF downloaded successfully
      },
      onError: (message) => {
        console.error('Failed to generate PDF:', message);
        alert('Failed to generate PDF. Please try again.');
      },
      onDevFallback: () => {
        alert('PDF download requires deployment. Use Print → Save as PDF for now.');
      }
    });
    setIsDownloading(false);
  };

  // Handle print using iframe for clean output
  const handlePrint = async () => {
    if (!reportRef.current) return;

    setIsPrinting(true);
    await printContent({
      contentRef: reportRef,
      onComplete: () => {
        setIsPrinting(false);
      }
    });
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 pt-20">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col">
        {/* Modal Header (not part of PDF) */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50 rounded-t-xl print:hidden">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-orange-500" />
            {isInstanceView ? 'Instance Report' : 'Job Report'}
          </h2>
          <div className="flex items-center gap-3">
            <button
              onClick={handleDownloadPDF}
              disabled={isDownloading}
              className={cn(
                "flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg transition-colors",
                isDownloading ? "opacity-50 cursor-not-allowed" : "hover:bg-orange-600"
              )}
            >
              <Download className="w-4 h-4" />
              {isDownloading ? 'Generating...' : 'Download PDF'}
            </button>
            <button
              onClick={handlePrint}
              disabled={isPrinting}
              className={cn(
                "flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg transition-colors",
                isPrinting ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-200"
              )}
            >
              <Printer className="w-4 h-4" />
              {isPrinting ? 'Preparing...' : 'Print'}
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Report Content (PDF area) */}
        <div className="flex-1 overflow-auto p-6">
          <div ref={reportRef} className="bg-white" style={{ minWidth: '700px' }}>
            {/* Report Header */}
            <div className="flex justify-between items-start mb-8 pb-6 border-b-2 border-orange-500">
              <div className="flex items-center gap-4">
                {/* Company Logo */}
                {company?.logo_url ? (
                  <img
                    src={company.logo_url}
                    alt={company?.company_name || 'Company'}
                    className="w-16 h-16 rounded-lg object-cover shadow-md"
                    crossOrigin="anonymous"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white text-xl font-bold shadow-md">
                    {companyInitials}
                  </div>
                )}
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">{company?.company_name || 'Company Name'}</h1>
                  {companyAddress && (
                    <p className="text-sm text-gray-500 mt-1">{companyAddress}</p>
                  )}
                  <div className="flex items-center gap-4 mt-1">
                    {companyPhone && (
                      <p className="text-xs text-gray-400 flex items-center gap-1">
                        <Phone className="w-3 h-3" /> {companyPhone}
                      </p>
                    )}
                    {companyEmail && (
                      <p className="text-xs text-gray-400 flex items-center gap-1">
                        <Mail className="w-3 h-3" /> {companyEmail}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-orange-500 mb-2">JOB REPORT</p>
                <p className="text-lg font-mono text-gray-700">{reportNumber}</p>
                <p className="text-sm text-gray-500 mt-2">
                  Generated: {new Date().toLocaleDateString('en-AU', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            </div>

            {/* Client Details (new) or Partner Agency (legacy) */}
            {(job?.client || partnerAgency || job?.partnerAgency) && (
              <div className="mb-6">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Client</h3>
                <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                  <div className="flex items-start gap-3">
                    <Building className="w-5 h-5 text-purple-600 mt-0.5" />
                    <div>
                      {/* New client structure */}
                      {job?.client ? (
                        <>
                          <p className="font-semibold text-gray-900">
                            {job.client.name || 'Client'}
                          </p>
                          {job.client.contact_person && (
                            <p className="text-sm text-gray-600 mt-1 flex items-center gap-2">
                              <User className="w-3 h-3" />
                              {job.client.contact_person}
                            </p>
                          )}
                          <div className="flex flex-wrap gap-4 mt-2">
                            {job.client.contact_email && (
                              <p className="text-sm text-gray-600 flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                {job.client.contact_email}
                              </p>
                            )}
                            {job.client.contact_phone && (
                              <p className="text-sm text-gray-600 flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {job.client.contact_phone}
                              </p>
                            )}
                          </div>
                          {job.clientReference && (
                            <p className="text-sm text-purple-700 mt-2">
                              Reference: {job.clientReference}
                            </p>
                          )}
                        </>
                      ) : (
                        /* Legacy: partnerAgency fallback */
                        <>
                          <p className="font-semibold text-gray-900">
                            {partnerAgency?.agency_name || job?.partnerAgency?.agency_name || 'Partner Agency'}
                          </p>
                          {(partnerAgency?.contact_person || job?.partnerAgency?.contact_person) && (
                            <p className="text-sm text-gray-600 mt-1 flex items-center gap-2">
                              <User className="w-3 h-3" />
                              {partnerAgency?.contact_person || job?.partnerAgency?.contact_person}
                            </p>
                          )}
                          <div className="flex flex-wrap gap-4 mt-2">
                            {(partnerAgency?.contact_email || job?.partnerAgency?.contact_email) && (
                              <p className="text-sm text-gray-600 flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                {partnerAgency?.contact_email || job?.partnerAgency?.contact_email}
                              </p>
                            )}
                            {(partnerAgency?.contact_phone || job?.partnerAgency?.contact_phone) && (
                              <p className="text-sm text-gray-600 flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {partnerAgency?.contact_phone || job?.partnerAgency?.contact_phone}
                              </p>
                            )}
                          </div>
                          {(partnerAgency?.reference_number || job?.partnerAgencyReference) && (
                            <p className="text-sm text-purple-700 mt-2">
                              Reference: {partnerAgency?.reference_number || job?.partnerAgencyReference}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Job Site & Job Summary */}
            <div className="grid grid-cols-2 gap-8 mb-8">
              {/* Job Site Section */}
              <div>
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Job Site</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  {(job?.jobSite || job?.serviceReceiver) ? (
                    <>
                      {/* New structure: jobSite */}
                      {job?.jobSite ? (
                        <>
                          <p className="font-semibold text-gray-900">{job.jobSite.name || job.jobSite.address || 'N/A'}</p>
                          {job.jobSite.address && job.jobSite.name && (
                            <p className="text-sm text-gray-600 mt-1 flex items-center gap-2">
                              <MapPin className="w-3 h-3" />
                              {job.jobSite.address}
                            </p>
                          )}
                          {job.jobSite.contact_person && (
                            <p className="text-sm text-gray-600 mt-1 flex items-center gap-2">
                              <User className="w-3 h-3" />
                              {job.jobSite.contact_person}
                            </p>
                          )}
                          {job.jobSite.contact_email && (
                            <p className="text-sm text-gray-600 mt-1 flex items-center gap-2">
                              <Mail className="w-3 h-3" />
                              {job.jobSite.contact_email}
                            </p>
                          )}
                          {job.jobSite.contact_phone && (
                            <p className="text-sm text-gray-600 mt-1 flex items-center gap-2">
                              <Phone className="w-3 h-3" />
                              {job.jobSite.contact_phone}
                            </p>
                          )}
                        </>
                      ) : (
                        /* Legacy fallback: serviceReceiver */
                        <>
                          <p className="font-semibold text-gray-900">{job.serviceReceiver.receiver_name || 'N/A'}</p>
                          {job.serviceReceiver.contact_person && (
                            <p className="text-sm text-gray-600 mt-1 flex items-center gap-2">
                              <User className="w-3 h-3" />
                              {job.serviceReceiver.contact_person}
                            </p>
                          )}
                          {job.serviceReceiver.contact_email && (
                            <p className="text-sm text-gray-600 mt-1 flex items-center gap-2">
                              <Mail className="w-3 h-3" />
                              {job.serviceReceiver.contact_email}
                            </p>
                          )}
                          {job.serviceReceiver.contact_phone && (
                            <p className="text-sm text-gray-600 mt-1 flex items-center gap-2">
                              <Phone className="w-3 h-3" />
                              {job.serviceReceiver.contact_phone}
                            </p>
                          )}
                          {job.serviceReceiver.address && (
                            <p className="text-sm text-gray-600 mt-1 flex items-center gap-2">
                              <MapPin className="w-3 h-3" />
                              {job.serviceReceiver.address}
                            </p>
                          )}
                        </>
                      )}
                    </>
                  ) : (
                    <p className="text-gray-500">No Job Site Assigned</p>
                  )}
                </div>
              </div>

              {/* Job Summary */}
              <div>
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Job Summary</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex flex-wrap gap-2 mb-3">
                    <span className={cn('px-2 py-1 rounded text-xs font-medium', isRecurring ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700')}>
                      {isRecurring ? 'Recurring' : 'One-Time'}
                    </span>
                    <span className={cn('px-2 py-1 rounded text-xs font-medium', getPriorityColor(job?.priority))}>
                      {job?.priority || 'Normal'} Priority
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Created:</span> {formatDate(job?.createdAt, preferences.date_format)}
                  </p>
                  {job?.updatedAt && (
                    <p className="text-sm text-gray-600 mt-1">
                      <span className="font-medium">Last Updated:</span> {formatDate(job?.updatedAt, preferences.date_format)}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Job Details */}
            <div className="mb-8">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Job Details</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">{job?.jobName || 'Untitled Job'}</h4>
                {job?.description && (
                  <p className="text-sm text-gray-600 mb-4">{job.description}</p>
                )}

                <div className="grid grid-cols-2 gap-4 mt-4">
                  {/* Location */}
                  <div className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 text-orange-500 mt-0.5" />
                    <div>
                      <p className="text-xs font-medium text-gray-400 uppercase">Location</p>
                      <p className="text-sm text-gray-900">{job?.jobSite?.address || job?.locationAddress || 'Not specified'}</p>
                    </div>
                  </div>

                  {/* Schedule */}
                  <div className="flex items-start gap-3">
                    <Calendar className="w-4 h-4 text-orange-500 mt-0.5" />
                    <div>
                      <p className="text-xs font-medium text-gray-400 uppercase">Schedule</p>
                      {isRecurring ? (
                        <div className="mt-1">
                          <WeekCircles schedule={schedule} />
                          {schedule?.pattern === 'fixed' && schedule?.time && (
                            <p className="text-xs text-gray-500 mt-1">Daily at {formatTime(schedule.time, preferences.time_format)}</p>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-900">
                          {formatDate(job?.scheduledDate, preferences.date_format)}
                          {job?.scheduledTime && ` at ${formatTime(job.scheduledTime, preferences.time_format)}`}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Estimated Duration */}
                  {estimatedHours > 0 && (
                    <div className="flex items-start gap-3">
                      <Clock className="w-4 h-4 text-orange-500 mt-0.5" />
                      <div>
                        <p className="text-xs font-medium text-gray-400 uppercase">Estimated Duration</p>
                        <p className="text-sm text-gray-900">{formatHoursMinutes(estimatedHours)} per visit</p>
                      </div>
                    </div>
                  )}

                  {/* Contractor */}
                  <div className="flex items-start gap-3">
                    <User className="w-4 h-4 text-orange-500 mt-0.5" />
                    <div>
                      <p className="text-xs font-medium text-gray-400 uppercase">Assigned Contractor</p>
                      <p className="text-sm text-gray-900">
                        {isInstanceView
                          ? (instance?.contractor?.full_name || instance?.contractor?.fullName || 'Unassigned')
                          : (job?.assignedContractor?.fullName || job?.assignedContractor?.full_name || 'Unassigned')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Site Contact */}
                {(job?.contactPersonName || job?.contactPersonPhone || job?.contactPersonEmail) && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <p className="text-xs font-medium text-gray-400 uppercase mb-2">Site Contact</p>
                    <div className="flex flex-wrap items-center gap-4">
                      {job.contactPersonName && (
                        <p className="text-sm text-gray-900 font-medium">{job.contactPersonName}</p>
                      )}
                      {job.contactPersonPhone && (
                        <p className="text-sm text-gray-600 flex items-center gap-1">
                          <Phone className="w-3 h-3" /> {job.contactPersonPhone}
                        </p>
                      )}
                      {job.contactPersonEmail && (
                        <p className="text-sm text-gray-600 flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {job.contactPersonEmail}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Requirements */}
                {(job?.photosRequiredBefore || job?.photosRequiredAfter || job?.clockInRadius) && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <p className="text-xs font-medium text-gray-400 uppercase mb-2">Requirements</p>
                    <div className="flex flex-wrap gap-2">
                      {job.photosRequiredBefore && (
                        <span className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded">
                          Photos Before
                        </span>
                      )}
                      {job.photosRequiredAfter && (
                        <span className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded">
                          Photos After
                        </span>
                      )}
                      {job.clockInRadius && (
                        <span className="px-2 py-1 bg-purple-50 text-purple-700 text-xs rounded">
                          Clock-in: {job.clockInRadius}m radius
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Safety & Equipment */}
            {(job?.safetyRequirements || job?.equipmentList) && (
              <div className="grid grid-cols-2 gap-6 mb-8">
                {job?.safetyRequirements && (
                  <div>
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                      <Shield className="w-4 h-4" /> Safety Requirements
                    </h3>
                    <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                      <p className="text-sm text-gray-700">{job.safetyRequirements}</p>
                    </div>
                  </div>
                )}

                {job?.equipmentList && (
                  <div>
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                      <Wrench className="w-4 h-4" /> Equipment Needed
                    </h3>
                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                      <p className="text-sm text-gray-700">{job.equipmentList}</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Other Details */}
            {job?.otherDetails && (
              <div className="mb-8">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" /> Additional Information
                </h3>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{job.otherDetails}</p>
                </div>
              </div>
            )}

            {/* Instance-Specific Section (for instance view) */}
            {isInstanceView && instance && (
              <div className="mb-8">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Instance Details</h3>
                <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-gray-500 uppercase">ID:</span>
                      <span className="text-sm font-bold text-orange-600 whitespace-nowrap">{job?.jobDisplayId || 'JOB'}-#{instance.instance_number}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-3.5 h-3.5 text-gray-400" />
                      <span className="text-sm text-gray-900">{formatDate(instance.scheduled_date, preferences.date_format)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-3.5 h-3.5 text-gray-400" />
                      <span className="text-sm text-gray-900">{instance.scheduled_time ? formatTime(instance.scheduled_time, preferences.time_format) : 'Not set'}</span>
                    </div>
                    {instance.contractor && (
                      <div className="flex items-center gap-2">
                        <User className="w-3.5 h-3.5 text-gray-400" />
                        <span className="text-sm text-gray-900">{instance.contractor.full_name || instance.contractor.fullName || 'Unassigned'}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Compliance Indicators */}
            {isInstanceView && hasReportData && (isWithinRadius !== null || timeCompliance) && (
              <div className="mb-8">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Compliance Indicators</h3>
                <div className="flex flex-wrap gap-4">
                  {/* Location Verification */}
                  {isWithinRadius !== null && (
                    <div className={cn(
                      'flex items-center gap-2 px-4 py-2 rounded-lg',
                      isWithinRadius ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                    )}>
                      {isWithinRadius ? (
                        <MapPinCheck className="w-5 h-5 text-green-600" />
                      ) : (
                        <MapPinOff className="w-5 h-5 text-red-600" />
                      )}
                      <div>
                        <p className={cn('text-sm font-medium', isWithinRadius ? 'text-green-700' : 'text-red-700')}>
                          {isWithinRadius ? 'Within Location Radius' : 'Outside Location Radius'}
                        </p>
                        <p className="text-xs text-gray-500">
                          {clockInDistance !== undefined ? `${Math.round(clockInDistance)}m from site` : ''}
                          {clockInRadius ? ` (${clockInRadius}m required)` : ''}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Time Compliance */}
                  {timeCompliance && (
                    <div className={cn(
                      'flex items-center gap-2 px-4 py-2 rounded-lg border',
                      timeCompliance.status === 'on-time' ? 'bg-green-50 border-green-200' :
                      timeCompliance.status === 'slight-overtime' ? 'bg-yellow-50 border-yellow-200' :
                      'bg-red-50 border-red-200'
                    )}>
                      <Timer className={cn(
                        'w-5 h-5',
                        timeCompliance.status === 'on-time' ? 'text-green-600' :
                        timeCompliance.status === 'slight-overtime' ? 'text-yellow-600' :
                        'text-red-600'
                      )} />
                      <div>
                        <p className={cn(
                          'text-sm font-medium',
                          timeCompliance.status === 'on-time' ? 'text-green-700' :
                          timeCompliance.status === 'slight-overtime' ? 'text-yellow-700' :
                          'text-red-700'
                        )}>
                          {timeCompliance.label}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatHoursMinutes(actualHours)} actual vs {formatHoursMinutes(estimatedHours)} estimated
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Report Details Section (for completed/submitted jobs) */}
            {isInstanceView && hasReportData && (
              <div className="mb-8">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Submitted Report Details</h3>
                <div className="space-y-4">
                  {/* Timesheet / Clock Times Section - NO FINANCIAL DATA */}
                  {(timesheet || instance?.clock_in_time || instance?.clock_out_time) && (
                    <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                      <h4 className="text-sm font-semibold text-green-800 mb-3 flex items-center gap-2">
                        <Clock className="w-4 h-4" /> Timesheet Details
                      </h4>
                      <div className={`grid gap-4 ${overtimeRequest?.overtime_hours ? 'grid-cols-4' : 'grid-cols-3'}`}>
                        <div>
                          <p className="text-xs font-medium text-gray-400 uppercase">Clock In</p>
                          <p className="text-sm text-gray-900 font-medium">
                            {formatFullDateTime(timesheet?.clock_in_time || instance?.clock_in_time, preferences.date_format, preferences.time_format)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-400 uppercase">Clock Out</p>
                          <p className="text-sm text-gray-900 font-medium">
                            {formatFullDateTime(timesheet?.clock_out_time || instance?.clock_out_time, preferences.date_format, preferences.time_format)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-400 uppercase">Total Duration</p>
                          <p className="text-sm text-gray-900 font-medium">
                            {timesheet?.total_hours ? formatHoursMinutes(parseFloat(timesheet.total_hours)) : 'N/A'}
                          </p>
                        </div>
                        {overtimeRequest?.overtime_hours && (
                          <div>
                            <p className="text-xs font-medium text-gray-400 uppercase">Overtime</p>
                            <p className="text-sm text-orange-600 font-medium">
                              {formatHoursMinutes(parseFloat(overtimeRequest.overtime_hours))}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Before Photos Section */}
                  <div className="photo-section bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <h4 className="text-sm font-semibold text-blue-800 mb-3 flex items-center gap-2">
                      <Camera className="w-4 h-4" /> Before Photos {beforePhotos.length > 0 && `(${beforePhotos.length})`}
                    </h4>
                    {beforePhotos.length > 0 ? (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {beforePhotos.map((photo, idx) => (
                          <div key={photo.id || idx} className="relative">
                            <div className="aspect-square bg-gray-200 rounded-lg overflow-hidden">
                              {photo.url || photo.storage_path ? (
                                <img
                                  src={photo.url || photo.storage_path}
                                  alt={`Before photo ${idx + 1}`}
                                  className="w-full h-full object-cover"
                                  crossOrigin="anonymous"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-400">
                                  <Camera className="w-8 h-8" />
                                </div>
                              )}
                            </div>
                            <span className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded text-xs font-medium bg-blue-500 text-white">
                              Before
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : job?.photosRequiredBefore ? (
                      <p className="text-sm text-orange-600 italic">Not submitted by contractor</p>
                    ) : (
                      <p className="text-sm text-gray-500 italic">Not required</p>
                    )}
                  </div>

                  {/* After Photos Section */}
                  <div className="photo-section bg-green-50 rounded-lg p-4 border border-green-200">
                    <h4 className="text-sm font-semibold text-green-800 mb-3 flex items-center gap-2">
                      <Camera className="w-4 h-4" /> After Photos {afterPhotos.length > 0 && `(${afterPhotos.length})`}
                    </h4>
                    {afterPhotos.length > 0 ? (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {afterPhotos.map((photo, idx) => (
                          <div key={photo.id || idx} className="relative">
                            <div className="aspect-square bg-gray-200 rounded-lg overflow-hidden">
                              {photo.url || photo.storage_path ? (
                                <img
                                  src={photo.url || photo.storage_path}
                                  alt={`After photo ${idx + 1}`}
                                  className="w-full h-full object-cover"
                                  crossOrigin="anonymous"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-400">
                                  <Camera className="w-8 h-8" />
                                </div>
                              )}
                            </div>
                            <span className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded text-xs font-medium bg-green-500 text-white">
                              After
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : job?.photosRequiredAfter ? (
                      <p className="text-sm text-orange-600 italic">Not submitted by contractor</p>
                    ) : (
                      <p className="text-sm text-gray-500 italic">Not required</p>
                    )}
                  </div>

                  {/* Other Photos Section */}
                  {otherPhotos.length > 0 && (
                    <div className="photo-section bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        <Camera className="w-4 h-4" /> Issue Photos ({otherPhotos.length})
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {otherPhotos.map((photo, idx) => (
                          <div key={photo.id || idx} className="relative">
                            <div className="aspect-square bg-gray-200 rounded-lg overflow-hidden">
                              {photo.url || photo.storage_path ? (
                                <img
                                  src={photo.url || photo.storage_path}
                                  alt={`Photo ${idx + 1}`}
                                  className="w-full h-full object-cover"
                                  crossOrigin="anonymous"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-400">
                                  <Camera className="w-8 h-8" />
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Work Notes Section */}
                  <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                    <h4 className="text-sm font-semibold text-purple-800 mb-3 flex items-center gap-2">
                      <MessageSquare className="w-4 h-4" /> Work Notes & Issues
                    </h4>
                    {(workNotes || instance?.work_notes) ? (
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">
                        {workNotes || instance?.work_notes}
                      </p>
                    ) : (
                      <p className="text-sm text-gray-500 italic">No notes provided</p>
                    )}
                  </div>

                  {/* Checklist Section */}
                  <div className="bg-teal-50 rounded-lg p-4 border border-teal-200">
                    <h4 className="text-sm font-semibold text-teal-800 mb-3 flex items-center gap-2">
                      <CheckSquare className="w-4 h-4" /> Checklist
                    </h4>
                    {(() => {
                      const data = checklistData || instance?.checklist_data;
                      const items = data ? (Array.isArray(data) ? data : (data?.items || Object.entries(data || {}).map(([key, value]) => ({ label: key, checked: value })))) : [];
                      const hasData = items && items.length > 0;

                      if (hasData) {
                        return (
                          <div className="space-y-2">
                            {items.map((item, idx) => (
                              <div key={idx} className="flex items-center gap-2">
                                <span className={cn(
                                  'w-5 h-5 rounded flex items-center justify-center text-xs',
                                  (item.checked || item.completed) ? 'bg-teal-500 text-white' : 'bg-gray-200 text-gray-400'
                                )}>
                                  {(item.checked || item.completed) ? '✓' : ''}
                                </span>
                                <span className={cn(
                                  'text-sm',
                                  (item.checked || item.completed) ? 'text-gray-900' : 'text-gray-500'
                                )}>
                                  {item.label || item.text || item.name || `Item ${idx + 1}`}
                                </span>
                              </div>
                            ))}
                          </div>
                        );
                      } else if (job?.checklistTemplate && job.checklistTemplate.length > 0) {
                        return <p className="text-sm text-orange-600 italic">Not submitted by contractor</p>;
                      } else {
                        return <p className="text-sm text-gray-500 italic">Not applicable</p>;
                      }
                    })()}
                  </div>

                  {/* Questionnaire Responses Section */}
                  <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
                    <h4 className="text-sm font-semibold text-indigo-800 mb-3 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" /> Questionnaire Responses
                    </h4>
                    {(() => {
                      const data = questionnaireResponses || instance?.questionnaire_responses;
                      const items = data ? (Array.isArray(data) ? data : Object.entries(data || {}).map(([question, answer]) => ({ question, answer }))) : [];
                      const hasData = items && items.length > 0;

                      if (hasData) {
                        return (
                          <div className="space-y-3">
                            {items.map((item, idx) => (
                              <div key={idx} className="border-b border-indigo-200 pb-2 last:border-0 last:pb-0">
                                <p className="text-xs font-medium text-indigo-600 uppercase">
                                  {item.question || item.label || `Question ${idx + 1}`}
                                </p>
                                <p className="text-sm text-gray-900 mt-1">
                                  {item.answer || item.response || item.value || 'No response'}
                                </p>
                              </div>
                            ))}
                          </div>
                        );
                      } else if (job?.questionnaireTemplate && job.questionnaireTemplate.length > 0) {
                        return <p className="text-sm text-orange-600 italic">Not submitted by contractor</p>;
                      } else {
                        return <p className="text-sm text-gray-500 italic">Not applicable</p>;
                      }
                    })()}
                  </div>
                </div>
              </div>
            )}

            {/* Instances Table (for recurring job-level view) */}
            {isRecurring && !isInstanceView && instances.length > 0 && (
              <div className="mb-8">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Scheduled Instances</h3>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Instance</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Scheduled Date</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Time</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Contractor</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {instances
                        .sort((a, b) => new Date(a.scheduled_date || 0) - new Date(b.scheduled_date || 0))
                        .map((inst, idx) => (
                          <tr key={inst.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="px-4 py-3 font-medium text-orange-600">#{inst.instance_number}</td>
                            <td className="px-4 py-3 text-gray-900">{formatDate(inst.scheduled_date, preferences.date_format)}</td>
                            <td className="px-4 py-3 text-gray-600">{inst.scheduled_time ? formatTime(inst.scheduled_time, preferences.time_format) : '-'}</td>
                            <td className="px-4 py-3 text-gray-900">{inst.contractor?.full_name || inst.contractor?.fullName || 'Unassigned'}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Summary Section (for recurring jobs) - NO FINANCIAL DATA */}
            {isRecurring && !isInstanceView && (
              <div className="mb-8">
                <div className="bg-gray-900 text-white rounded-lg p-6">
                  <div className="flex justify-end">
                    <div className="w-64 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">Total Instances:</span>
                        <span className="font-medium">{totalInstances}</span>
                      </div>
                      {estimatedHours > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-400">Est. Hours:</span>
                          <span className="font-medium">{formatHoursMinutes(estimatedHours)} × {totalInstances} = {formatHoursMinutes(totalHours)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="border-t-2 border-gray-200 pt-6 mt-8 pb-8">
              <div className="flex justify-between text-xs text-gray-400">
                <div>
                  <p>Created: {formatDate(job?.createdAt, preferences.date_format)}</p>
                  {job?.updatedAt && <p>Last Updated: {formatDate(job?.updatedAt, preferences.date_format)}</p>}
                </div>
                <div className="text-right">
                  <p>Job Report generated by Mervo</p>
                  <p>{new Date().toLocaleString('en-AU')}</p>
                </div>
              </div>
              {!hasReportData && (
                <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                  <p className="text-xs text-yellow-700">
                    <strong>Note:</strong> This is a job summary. Complete report details will be available after contractor submission.
                  </p>
                </div>
              )}
              {/* Powered by Mervo */}
              <div className="flex items-center justify-center gap-2 py-4 mt-4">
                <span className="text-xs text-gray-400">Powered by</span>
                <div className="w-5 h-5 bg-gradient-to-br from-orange-500 to-orange-600 rounded flex items-center justify-center text-white text-xs font-bold">
                  M
                </div>
                <span className="text-xs text-gray-500 font-semibold">Mervo</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
